import { Close as CloseIcon } from '@styled-icons/material';
import { BorderBox, Button, Position, Rhythm } from '@trmediaab/zebra';
import type { PaywallReason } from '@trmediaab/zebra-types/trais';
import type {
  Campaign,
  CheckoutSite,
  Product,
} from '@trmediaab/zebra-types/trhub';
import { brands } from '@trmediaab/zebra-utils';

import type { ProductOfferProps } from './ProductOffer';
import ProductOffer from './ProductOffer';
import ReasonInfo from './ReasonInfo';

interface PaywallProps extends Omit<ProductOfferProps, 'product' | 'campaign'> {
  authUrl: string;
  reason: PaywallReason;
  contentType: 'article' | 'service';
  products: Product[];
  campaigns: Campaign[];
  onCancel?: () => void;
}

const createMessages = (
  site: CheckoutSite,
  contentType: PaywallProps['contentType'],
) => {
  const brandSite = site === 'jokersystemet_no' ? 'jokersystemet' : site;

  if (['jokersystemet', 'jokersystemet_no'].includes(site)) {
    return [
      `Du har ett gratiskonto. För ${
        contentType === 'article'
          ? 'att läsa den här artikeln'
          : 'nyttja denna funktion'
      } behöver du ett betalabonnemang på Jokersystemet. Har du en pausad prenumeration? Återaktivera den enkelt via Mina sidor.`,
      contentType === 'article'
        ? 'Det här är en låst artikel. Logga in med ett betalabonnemang för att fortsätta läsa.'
        : `Det här är en tjänst för ${
            brands.getBrand(brandSite).name
          }s betalabonnenter. Logga in för att fortsätta.`,
    ] as const;
  }

  return [
    `Du har ett gratiskonto. För ${
      contentType === 'article'
        ? 'att läsa den här artikeln'
        : 'den här tjänsten'
    } behöver du en digital prenumeration. Har du en pausad prenumeration? Återaktivera den enkelt via Mina sidor.`,
    contentType === 'article'
      ? 'Det här är en låst artikel. Logga in som prenumerant för att fortsätta läsa.'
      : `Det här är en tjänst för ${
          brands.getBrand(brandSite).name
        }s prenumeranter. Logga in för att fortsätta.`,
  ] as const;
};

export const ctaButtonWidths = {
  width: { _: '100%', sm: 'auto' },
  maxWidth: { _: '260px', sm: 'none' },
  minWidth: { sm: '260px' },
};

const Paywall = ({
  authUrl,
  reason,
  contentType,
  site,
  products,
  campaigns,
  orderId,
  returnUrl,
  returnUrlText,
  disallowUserChange,
  analytics,
  onCancel,
  onAuthentication,
}: PaywallProps) => {
  // Expected to be true but if something went wrong downstream or if
  // no products or campaigns has been promoted in admin we'll get nothing.
  const hasAvailableOffer = products.length > 0 || campaigns.length > 0;

  const [campaign] = campaigns;

  // TODO - what if a campaign has multiple products?
  const product =
    campaign != null
      ? campaign.products[0]
      : products.find(p => p.popular) || products[0];

  const [insufficientPrivilegeMessage, loggedOutMessage] = createMessages(
    site,
    contentType,
  );

  let offerComponent = null;

  if (hasAvailableOffer) {
    const props = {
      orderId,
      returnUrl,
      returnUrlText,
      disallowUserChange,
      analytics,
      onAuthentication,
      site,
    };

    if (product != null && campaign != null) {
      offerComponent = (
        <ProductOffer campaign={campaign} product={product} {...props} />
      );
    } else if (product != null) {
      offerComponent = <ProductOffer product={product} {...props} />;
    } else if (campaign != null) {
      offerComponent = <ProductOffer campaign={campaign} {...props} />;
    }
  }

  return (
    <BorderBox
      border="1"
      css={`
        position: relative;
      `}
    >
      <Rhythm between="5">
        <Rhythm
          between="5"
          px={{ _: 3, sm: 4, md: 5 }}
          pt="4"
          css={`
            text-align: center;
          `}
        >
          <ReasonInfo
            authUrl={authUrl}
            reason={reason}
            insufficientPrivilegeMessage={insufficientPrivilegeMessage}
            loggedOutMessage={loggedOutMessage}
            hasOffer={hasAvailableOffer}
            ctaButtonWidths={ctaButtonWidths}
          />
        </Rhythm>
        {offerComponent}
      </Rhythm>
      {onCancel != null && (
        <Position.absolute right="2,5" top="2,5">
          <Button color="link.primary" onClick={onCancel}>
            <CloseIcon width="24px" />
          </Button>
        </Position.absolute>
      )}
    </BorderBox>
  );
};

export default Paywall;
